/**
 * Language Validators — Text, spelling, grammar, and sentence analysis question types.
 *
 * Extracted from validatorRegistry.ts during R1-S1 god-file split.
 * Covers: text_highlight, true_false_series, error_spotting, sentence_parser,
 *         comparison_judge, verb_conjugation_flow, sentence_error_judge.
 */

import type { ValidatorFn } from './shared';

// ── Text Highlight ───────────────────────────────────────────

export const validateTextHighlight: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {};

  // Support both explicit segments AND text+correctHighlights format
  let segments: Array<{
    id: string;
    text: string;
    isCorrect?: boolean;
  }> = config.segments || [];
  if (segments.length === 0 && config.text) {
    // Build segments from text + correctHighlights (same logic as renderer)
    const text: string = config.text;
    const rawHighlights: Array<{
      start: number;
      end: number;
    } | string> = config.correctHighlights || [];

    // Normalise: support both {start,end} objects and plain word strings
    const correctHighlights: Array<{
      start: number;
      end: number;
    }> = [];
    for (const h of rawHighlights) {
      if (typeof h === 'object' && h !== null && 'start' in h && 'end' in h) {
        correctHighlights.push(h as {
          start: number;
          end: number;
        });
      } else if (typeof h === 'string') {
        const word = h as string;
        const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
          correctHighlights.push({
            start: match.index,
            end: match.index + match[0].length
          });
        }
      }
    }
    const wordRegex = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = wordRegex.exec(text)) !== null) {
      const wordStart = match.index;
      const wordEnd = match.index + match[0].length;
      const isCorrect = correctHighlights.some((h: {
        start: number;
        end: number;
      }) => wordStart < h.end && wordEnd > h.start);
      segments.push({
        id: `word-${wordStart}`,
        text: match[0],
        isCorrect
      });
    }
  }

  // Determine if this is a multi-select answer (JSON array) or single segment ID
  let selectedSegmentIds: string[];
  try {
    const parsed = JSON.parse(String(userAnswer));
    if (Array.isArray(parsed)) {
      selectedSegmentIds = parsed;
    } else {
      selectedSegmentIds = [String(userAnswer)];
    }
  } catch {
    selectedSegmentIds = [String(userAnswer)];
  }
  const correctSegments = segments.filter(s => s.isCorrect === true);
  const correctSegmentIds = new Set(correctSegments.map(s => s.id));
  const selectedSet = new Set(selectedSegmentIds);

  // Multi-select: all correct segments selected AND no incorrect ones
  if (correctSegments.length > 1 || selectedSegmentIds.length > 1) {
    const allCorrectSelected = [...correctSegmentIds].every(id => selectedSet.has(id));
    const noIncorrectSelected = [...selectedSet].every(id => correctSegmentIds.has(id));
    const isCorrect = allCorrectSelected && noIncorrectSelected;
    const selectedTexts = selectedSegmentIds.map(id => segments.find(s => s.id === id)?.text).filter(Boolean);
    const correctTexts = correctSegments.map(s => s.text);
    return {
      isCorrect,
      distractorIndex: isCorrect ? undefined : 0,
      displayAnswer: selectedTexts.join(', '),
      correctAnswerDisplay: correctTexts.join(', ')
    };
  }

  // Single-select fallback
  const selectedSegmentIndex = segments.findIndex(s => s.id === selectedSegmentIds[0]);
  const selectedSegment = segments[selectedSegmentIndex];
  if (!selectedSegment) return {
    isCorrect: false,
    distractorIndex: 0
  };
  return {
    isCorrect: selectedSegment.isCorrect === true,
    distractorIndex: selectedSegmentIndex,
    displayAnswer: selectedSegment.text,
    correctAnswerDisplay: correctSegments.length === 1 ? correctSegments[0].text : undefined
  };
};

// ── True/False Series ────────────────────────────────────────

export const validateTrueFalseSeries: ValidatorFn = (userAnswer, question) => {
  let userAnswers: boolean[] = [];
  try {
    userAnswers = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  const statements = config.statements || [];

  // Count every matching statement independently. (Previously this used
  // Array.every(), which short-circuits on the first mismatch — so a single
  // wrong statement made correctCount stop early and report "0 correct" even
  // when later statements were right.)
  const correctCount = statements.reduce((count: number, stmt: {
    isTrue: boolean;
  }, idx: number) => userAnswers[idx] === stmt.isTrue ? count + 1 : count, 0);
  const isCorrect = userAnswers.length === statements.length && correctCount === statements.length;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${statements.length} stellingen goed`
  };
};

// ── Error Spotting ───────────────────────────────────────────

export const validateErrorSpotting: ValidatorFn = (userAnswer, question) => {
  let userCorrections: Record<number, string> = {};
  try {
    userCorrections = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  const errors: Array<{
    word: string;
    position: number;
    correction: string;
    rule?: string;
  }> = config.errors || [];
  let correctCount = 0;
  const isCorrect = Object.keys(userCorrections).length === errors.length && errors.every(error => {
    const userCorrection = userCorrections[error.position];
    if (!userCorrection) return false;
    const match = userCorrection.toLowerCase().trim() === error.correction.toLowerCase().trim();
    if (match) correctCount++;
    return match;
  });
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${errors.length} fouten verbeterd`
  };
};

// ── Sentence Parser ──────────────────────────────────────────

export const validateSentenceParser: ValidatorFn = (userAnswer, question) => {
  let userAssignments: Record<string, string> = {};
  try {
    userAssignments = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data?.sentenceParser || question.data as Record<string, unknown> || {};
  const correctAssignments = config.correctAssignments || {};
  const correctKeys = Object.keys(correctAssignments);
  let correctCount = 0;
  const isCorrect = correctKeys.length > 0 && correctKeys.every(key => {
    const match = userAssignments[key] === correctAssignments[key];
    if (match) correctCount++;
    return match;
  }) && Object.keys(userAssignments).length === correctKeys.length;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${correctKeys.length} woorden correct benoemd`
  };
};

// ── Comparison Judge ─────────────────────────────────────────

export const validateComparisonJudge: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer);
  const config = question.data?.comparisonJudge || question.data as Record<string, unknown> || {};
  const correctIndex = config.correctIndex ?? -1;
  const isCorrect = selectedIndex === correctIndex;
  const items = config.items || [];
  const selectedLabel = items[selectedIndex]?.content || '?';
  const correctLabel = items[correctIndex]?.content || '?';
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : selectedIndex,
    displayAnswer: isCorrect ? correctLabel : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel
  };
};

// ── Verb Conjugation Flow ────────────────────────────────────

/**
 * Reconstructs the per-step truth from `data.steps` and compares it against
 * the renderer payload, which is a JSON `Record<stepIndex, userValue>`.
 *
 * GHOST-ERROR FIX: the previous implementation compared the JSON payload to
 * the top-level `correctAnswer` field, which in every seed is a placeholder
 * integer (e.g. `0`). That made the comparison ALWAYS false — a correct
 * answer rendered green per-step but scored as wrong. The truth lives in
 * `data.steps[].options[].isCorrect` (choice) / `data.steps[].acceptedAnswers`
 * (input), exactly like the renderer's own `isStepCorrect()`. We mirror that
 * logic here so renderer and scorer can no longer drift.
 */
export const validateVerbConjugationFlow: ValidatorFn = (userAnswer, question) => {
  const raw = (question.data ?? {}) as Record<string, unknown>;
  const config = (raw.verbConjugationFlow as Record<string, unknown> || raw) as {
    steps?: Array<{
      type?: 'choice' | 'input';
      options?: Array<{
        value: string;
        isCorrect?: boolean;
      }>;
      acceptedAnswers?: string[];
    }>;
    finalAnswer?: string;
  };
  const steps = Array.isArray(config.steps) ? config.steps : [];

  // Defensive fallback: no structured steps → preserve legacy string compare.
  if (steps.length === 0) {
    return {
      isCorrect: String(userAnswer).trim().toLowerCase() === String(question.correctAnswer).trim().toLowerCase()
    };
  }
  let userSteps: Record<string, string> = {};
  try {
    const parsed = JSON.parse(String(userAnswer));
    if (parsed && typeof parsed === 'object') {
      userSteps = parsed as Record<string, string>;
    }
  } catch {

    /* malformed payload → treated as all-empty below */}
  const isStepCorrect = (index: number): boolean => {
    const step = steps[index];
    const userVal = String(userSteps[index] ?? '').trim();
    if (!step) return false;
    if (step.type === 'choice') {
      const correctOption = step.options?.find(opt => opt.isCorrect);
      return !!correctOption && correctOption.value === userVal;
    }
    const accepted = step.acceptedAnswers || [];
    return accepted.some(ans => String(ans).trim().toLowerCase() === userVal.toLowerCase());
  };
  const isCorrect = steps.every((_, index) => isStepCorrect(index));
  return {
    isCorrect,
    displayAnswer: config.finalAnswer || steps.map((step, index) => step.type === 'choice' ? step.options?.find(o => o.isCorrect)?.value ?? '' : step.acceptedAnswers?.[0] ?? '').filter(Boolean).join(' → ')
  };
};

// ── Sentence Error Judge ─────────────────────────────────────

export const validateSentenceErrorJudge: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer);
  const config = question.data || {};
  const sentences: Array<{
    text: string;
    hasError: boolean;
    correction?: string;
  }> = config.sentences as Array<{
    text: string;
    hasError: boolean;
    correction?: string;
  }> || [];
  if (selectedIndex < 0 || selectedIndex >= sentences.length) {
    return {
      isCorrect: false,
      distractorIndex: 0
    };
  }
  const selected = sentences[selectedIndex];
  const isCorrect = selected?.hasError === true;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : selectedIndex,
    displayAnswer: selected?.text || '?'
  };
};